import { CircleScoreFluid } from '@/components/client/score/circle-score-fluid'
import { Anchor } from '@/components/client/seo/anchor'
import { formatPrice } from '@/lib/utils/format-price'

import type { LevelPriceInfo, ModelPanelData, TranslateFn } from './panel-types'
import { SpecsList } from './specs-list'

// AffiliateAction attributes for the panel's two offer links. Only applied when
// the offer URL exists — the price text still renders as a dead '#' link without
// one, and that click must not land in the affiliate funnel.
const affiliateAttrs = (
  levelPrice: LevelPriceInfo,
  releaseSlug: string,
  position: string,
  cta: string
): Record<string, string | boolean> =>
  levelPrice.url
    ? {
        'data-track': true,
        'data-event-name': 'AffiliateAction',
        'data-event-seller': levelPrice.sellerName ?? '',
        'data-affiliate-seller-type': levelPrice.sellerType ?? 'affiliate',
        'data-event-product': releaseSlug,
        'data-event-position': position,
        'data-affiliate-cta': cta,
        'data-affiliate-text': cta,
        'data-affiliate-test-name': 'model-comparison',
        'data-event-url': levelPrice.url,
      }
    : { 'data-tracking': 'ignore' }

// Presentational, isomorphic panel. No `'use client'` and no server-only imports,
// so it renders on the server for the default panel and inside the client tree for
// on-demand-fetched panels. Tooltip ids are precomputed by the builder and read off
// each row (`row.tooltipId`) — there is no `registerTooltip` callback here anymore.
export const SpecsPanel = ({
  specs,
  modelId,
  levelId,
  modelName,
  modelShortName,
  modelImage,
  score,
  levelPrice,
  uniqueToModel,
  upgradeHighlights,
  levelName,
  releaseSlug,
  hidden,
  t,
}: ModelPanelData & { hidden: boolean; t: TranslateFn }) => (
  <div data-model-id={modelId} data-level-id={levelId} style={hidden ? { display: 'none' } : undefined}>
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/50">
      {/* Product header */}
      <div className="flex flex-wrap items-center gap-5 p-5 lg:flex-nowrap lg:gap-6 lg:p-6">
        {modelImage && (
          <div className="w-[80px] flex-shrink-0 lg:w-[110px]">
            <img src={modelImage} alt={modelName} className="h-full w-full object-contain" loading="lazy" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-navy text-base font-bold tracking-tight lg:text-lg">{modelName}</h3>
              {levelPrice && (
                <Anchor
                  href={levelPrice.url || '#'}
                  {...affiliateAttrs(
                    levelPrice,
                    releaseSlug,
                    'model-comparison-price',
                    `${formatPrice(levelPrice.price)} ${t('price.perPair', '/ pair')}`
                  )}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-lapis mt-0.5 inline-flex items-center gap-1 text-sm font-semibold hover:underline"
                >
                  {formatPrice(levelPrice.price)} {t('price.perPair', '/ pair')}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-slate-400"
                  >
                    <path d="M15 3h6v6" />
                    <path d="M10 14 21 3" />
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  </svg>
                </Anchor>
              )}
            </div>
            {score != null && score > 0 && (
              <CircleScoreFluid
                size="md"
                progress={(score / 5) * 100}
                amount={score}
                className="flex-shrink-0 shadow-sm"
                origin="model-comparison/card"
              />
            )}
          </div>
        </div>
        {/* View Details CTA */}
        {levelPrice?.url && (
          <Anchor
            href={levelPrice.url}
            {...affiliateAttrs(levelPrice, releaseSlug, 'model-comparison', t('product.viewDetails', 'View Details'))}
            data-track-impression
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="bg-lapis hover:bg-navy hidden flex-shrink-0 items-center gap-1.5 rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-150 hover:shadow-md active:scale-[0.97] sm:flex"
          >
            {t('product.viewDetails', 'View Details')}
            <svg width="12" height="12" viewBox="0 0 512 512" fill="white">
              <path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0-201.4 201.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3 448 192c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 96C35.8 96 0 131.8 0 176L0 432c0 44.2 35.8 80 80 80l256 0c44.2 0 80-35.8 80-80l0-80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 80c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l80 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 96z" />
            </svg>
          </Anchor>
        )}
      </div>

      {/* Unique to form factor */}
      {uniqueToModel.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/40 px-5 py-3.5 lg:px-6">
          <p className="mb-2 text-[11px] font-medium tracking-wide text-slate-400 uppercase">
            {t('product.uniqueTo', 'Unique to {{modelName}}', { modelName: modelShortName })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {uniqueToModel.map((spec) => (
              <span
                key={`unique-${spec.genericName}`}
                className="text-navy cursor-help rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors hover:border-slate-300 hover:bg-slate-50"
                data-spec-tooltip-id={spec.tooltipId}
              >
                {spec.name}
                {spec.value !== '✓' ? `: ${spec.value}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Upgrade Highlights */}
      {upgradeHighlights.length > 0 && levelName && (
        <div className="border-t border-slate-100 bg-slate-50/40 px-5 py-3.5 lg:px-6">
          <p className="mb-2 text-[11px] font-medium tracking-wide text-slate-400 uppercase">
            {t('product.upgradeHighlights', 'Upgrade Highlights for {{levelName}}', { levelName: levelName ?? '' })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {upgradeHighlights.map((spec) => (
              <span
                key={`upgrade-${spec.name}`}
                className="text-navy cursor-help rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors hover:border-slate-300 hover:bg-slate-50"
                data-spec-tooltip-id={spec.tooltipId}
              >
                {spec.name}
                {spec.value !== '✓' ? `: ${spec.value}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Specs table */}
      {specs.length > 0 && <SpecsList specs={specs} />}
    </div>
  </div>
)
